import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DotCMSGraphQLParams, DotCMSPageAsset, DotCMSPageRequestParams } from '@dotcms/types';
import { getUVEState } from '@dotcms/uve';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import {
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

import { AppFacade } from 'ish-core/facades/app.facade';
import { DotCmsUveConfig } from 'ish-core/models/dot-cms-uve/dot-cms-uve-config.model';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import {
  applyConfiguration,
  getCurrentDotCmsApiConfiguration,
  getCurrentDotCmsFooterIdentifier,
  getCurrentDotCmsPersona,
  getCurrentLocale,
  getDotCmsLanguageConfiguration,
} from 'ish-core/store/core/configuration';
import { selectCleanUrl, selectPreviousUrl, selectRouter } from 'ish-core/store/core/router/router.selectors';
import { getServerConfig } from 'ish-core/store/core/server-config';
import { setIsTransparentHeader } from 'ish-core/store/core/viewconf';
import { whenTruthy } from 'ish-core/utils/operators';

import { ExtraContent, VideoContentlet } from '../models/dot-cms-uve-contentlet.model';
import { PageState } from '../models/dot-cms-uve.model';
import { DotCmsUveEditablePageService } from '../services/dot-cms-uve-editable-page/dot-cms-uve-editable-page.service';
import {
  BASE_EXTRA_QUERIES,
  createNavigationQuery,
  createPageQuery,
  footerQuery,
  fragmentNav,
  fragmentPage,
} from '../shared/queries/queries';
import { isDotCmsPage } from '../utils/dot-cms-uve-utils';

type DotCMSPage = {
  pageAsset: DotCMSPageAsset;
  content: ExtraContent;
};

interface Deps {
  personaId: string | undefined;
  languageId: number | undefined;
  footerIdentifier: string | undefined;
  url: string | undefined;
}

/* eslint-disable @typescript-eslint/member-ordering */
@Injectable({ providedIn: 'root' })
export class DotCmsUveFacade {
  private store = inject(Store);
  private appFacade = inject(AppFacade);
  private router = inject(Router);

  private editablePageService = inject<DotCmsUveEditablePageService<DotCMSPage>>(DotCmsUveEditablePageService);

  // State and signals
  private readonly _pageState = signal<PageState<DotCMSPage> | null>(null);
  readonly pageState = computed(() => this._pageState());
  private initializedOnce = false;
  lastDeps: Deps | null = null;
  private currentPageSignal = signal<Signal<PageState<DotCMSPage> | null> | null>(null);
  readonly $pageUrl = toSignal(
    this.store.pipe(
      select(selectCleanUrl),
      filter(
        url =>
          url !== undefined && /^\/(page\/.+|home)$/.test(url) && isDotCmsPage(url) && /^\/(home|page\/.+)$/.test(url)
      ),
      distinctUntilChanged(isEqual),
      map(url => (url === '/home' ? '/index' : (url.replace(/^\/page(\/.*)$/, '$1') as string)))
    )
  );
  private readonly currentLocale$ = this.store.pipe(select(getCurrentLocale), whenTruthy());

  private readonly languageId$ = this.currentLocale$.pipe(
    concatLatestFrom(() => this.store.pipe(select(getDotCmsLanguageConfiguration))),
    map(([locale, config]) => config?.[locale])
  );

  // Wait for the ICM server configuration: it supplies the footer identifier and the persona. The page request
  // itself already waited for it (DotCmsUvePageService.getPageAsset), so a query built earlier had no footer, and
  // the configuration then sent a second, complete query.
  private readonly deps$ = this.store.pipe(
    select(getServerConfig),
    whenTruthy(),
    take(1),
    switchMap(() =>
      combineLatest([
        this.store.pipe(select(getCurrentDotCmsPersona), distinctUntilChanged()),
        this.languageId$.pipe(distinctUntilChanged()),
        this.store.pipe(select(getCurrentDotCmsFooterIdentifier), distinctUntilChanged()),
        this.store.pipe(select(selectCleanUrl), distinctUntilChanged()),
      ])
    ),
    debounceTime(200),
    distinctUntilChanged(isEqual),
    shareReplay(1),
    map(([personaId, languageId, footerIdentifier, pageUrl]) => {
      this.applyLanguageConfiguration();
      const url = pageUrl === '/home' ? '/index' : pageUrl;
      return {
        personaId,
        languageId,
        footerIdentifier,
        url,
      };
    })
  );

  readonly depsSignal = toSignal(this.deps$, { initialValue: null });
  serverConfigLoaded$ = this.store.pipe(select(getServerConfig)).pipe(map(config => !!config));

  constructor() {
    // Effect to handle dependency changes and create pageSignal
    effect(
      () => {
        const deps = this.depsSignal();
        if (!deps || (this.lastDeps && isEqual(this.lastDeps, deps))) {
          this.editablePageService.setMetaData();
          return;
        }
        if (
          (!this.initializedOnce || (deps.url?.includes('/page') && isDotCmsPage(deps.url)) || deps.url === '/index') &&
          deps.url
        ) {
          this.initializedOnce = true;
          this.lastDeps = deps;
          this.initializePageSignal(deps);
        }
      },
      { allowSignalWrites: true }
    );

    // Effect to update _pageState
    effect(
      () => {
        const pageSignal = this.currentPageSignal()?.();
        if (!pageSignal) {
          return;
        }

        try {
          this._pageState.set(pageSignal);
        } catch (error) {
          console.error('Failed to update page state:', error);
          this._pageState.set({ status: 'error', error: undefined });
        }
      },
      { allowSignalWrites: true }
    );
  }

  private initializePageSignal(deps: Deps): void {
    const graphqlQueries = this.buildGraphQLQueries(deps);
    const pageRequestMode = this.getPageRequestMode();
    const initConfig: DotCMSPageRequestParams = {
      graphql: graphqlQueries,
      ...(deps.personaId && { personaId: deps.personaId }),
      ...(deps.languageId && { languageId: deps.languageId }),
      // Always sent, never conditional: the editor overrides this key on its re-run, and a key
      // that is absent from the shipped variables is one it cannot override.
      mode: pageRequestMode,
    };

    try {
      const rawPageSignal = this.editablePageService.initializePage(initConfig, deps.url!);
      let lastState: PageState<DotCMSPage> | null = null;
      const deduplicatedPageSignal = computed(() => {
        const state = rawPageSignal();

        if (!state || state.status === 'loading') {
          return null;
        }
        if (!state || state.status === 'error') {
          this.navigateTo('/error');
          this.lastDeps = {
            ...this.lastDeps,
            url: '/error',
          };
          return null;
        }
        if (lastState && isEqual(this.normalizeState(lastState), this.normalizeState(state))) {
          return null;
        }
        lastState = state;
        return this.mergePageState(state);
      });

      this.currentPageSignal.set(deduplicatedPageSignal);
    } catch (error) {
      console.error('Failed to initialize page signal:', error);
      this._pageState.set({ status: 'error', error: undefined });
    }
  }

  /**
   * The page mode the STOREFRONT puts on its own request. Always 'LIVE' — including inside the
   * editor.
   *
   * THIS MUST NOT MIRROR THE UVE MODE. It used to: `getUVEState()?.mode` was mapped back to its
   * `UVE_MODE` key, so a page loaded inside the editor asked dotCMS for 'EDIT_MODE' over the
   * storefront's own connection. That connection is authenticated with the delivery token, whose
   * user (`Auditor`) has no read permission on pages carrying individual permission rows, so
   * dotCMS answered `data.page: null` + `PERMISSION_DENIED`. That error object was then handed to
   * `listen()` -> `initUVE` -> `setClientIsReady`, which posts it verbatim as the CLIENT_READY
   * payload; carrying no `graphql`, it never installed our query (dotCMS's handler is
   * `if (query) setCustomClient(...)`), and the editor sat on a permanent spinner with nothing
   * logged on either side.
   *
   * The editor does not need us to request EDIT_MODE, because it never renders the page asset we
   * send it. `initUVE` ships our response as the CLIENT_READY payload and dotCMS's
   * `dot-uve-actions-handler.service.ts` takes only the `graphql` off it:
   *
   *     if (query) uveStore.setCustomClient({ query, variables });
   *     uveStore.pageReload(pageParams);
   *
   * — it re-runs OUR query itself, overriding `mode` with 'EDIT_MODE', in the logged-in author's
   * own session, and pushes the result back over `uve-set-page-data`. So the working version comes
   * from the author's permissions, which is where it should come from; the delivery token is never
   * asked for authoring rights.
   *
   * THE VARIABLE MUST BE NAMED `mode`. The editor merges `{ ...storedVariables, url, mode, ... }`,
   * so a differently-named key (`pageMode`) survives the merge untouched and the editor's
   * 'EDIT_MODE' lands on a variable the document never declared — GraphQL discards it and every
   * editor reload silently re-fetches LIVE. `client.page.get()` declares `$mode` and passes
   * `page(pageMode: $mode)`, so going through the SDK keeps this correct.
   *
   * Verified against dss.dotcms.io: the Next.js storefront on the same instance, whose service
   * user holds the same `Auditor` role with the same grants, renders in the UVE because it does
   * exactly this. See docs/dotcms-uve-integration.md.
   */
  private getPageRequestMode(): DotCMSPageRequestParams['mode'] {
    return 'LIVE';
  }

  private buildGraphQLQueries(deps: Deps): DotCMSGraphQLParams {
    const currentPageState = this._pageState();
    //@ts-ignore
    const hasFooter = currentPageState?.pageResponse?.content?.footer !== undefined;
    const hasHeaderNavigation = currentPageState?.pageResponse?.content?.navigation !== undefined;
    const isEditMode = getUVEState()?.mode === 'EDIT_MODE';
    const graphqlQueries: DotCMSGraphQLParams = { ...BASE_EXTRA_QUERIES };

    if (deps.footerIdentifier && (isEditMode || !hasFooter)) {
      graphqlQueries.content = {
        ...graphqlQueries.content,
        footer: footerQuery(deps.footerIdentifier),
      };
    }

    if (!hasHeaderNavigation) {
      graphqlQueries.content = {
        ...graphqlQueries.content,
        navigation: createNavigationQuery(5),
      };
      graphqlQueries.fragments = [...(graphqlQueries.fragments || []), fragmentNav];
    }

    let pagePath = deps.url;

    if (pagePath === '/home') {
      pagePath = '/index';
    } else if (pagePath.startsWith('/page/')) {
      // Extract everything after "/page/"
      const subPath = pagePath.split('/page/')[1];
      pagePath = `/${subPath}`; // optional leading slash
    }

    graphqlQueries.content = {
      ...graphqlQueries.content,
      extraPageProps: createPageQuery(pagePath), // or dynamic path
    };

    graphqlQueries.fragments = [...(graphqlQueries.fragments || []), fragmentPage];

    return graphqlQueries;
  }

  private mergePageState(newState: PageState<DotCMSPage>): PageState<DotCMSPage> {
    const currentPageState = this._pageState();
    return {
      ...newState,
      pageResponse: {
        ...newState.pageResponse,
        //@ts-ignore
        content: {
          ...(currentPageState?.pageResponse?.content || {}),
          //@ts-ignore
          ...(newState.pageResponse?.content?.footer !== undefined
            ? //@ts-ignore
              { footer: newState.pageResponse.content.footer }
            : {}),
          ...(newState.pageResponse?.content?.navigation !== undefined
            ? { navigation: newState.pageResponse.content.navigation }
            : {}),
          ...(newState.pageResponse?.content?.extraPageProps !== undefined
            ? { extraPageProps: newState.pageResponse.content.extraPageProps }
            : {}),
        },
      },
    };
  }

  private normalizeState(state: PageState<DotCMSPage> | null) {
    if (!state) {
      return null;
    }
    return {
      ...state,
      pageResponse: state.pageResponse
        ? {
            ...state.pageResponse,
            pageAsset: state.pageResponse.pageAsset
              ? {
                  ...state.pageResponse.pageAsset,
                  page: state.pageResponse.pageAsset.page ? { ...state.pageResponse.pageAsset.page } : undefined,
                }
              : undefined,
            graphql: state.pageResponse.graphql ? { ...state.pageResponse.graphql } : undefined,
          }
        : undefined,
    };
  }

  private applyLanguageConfiguration(): void {
    let languageConfig: DotCmsUveConfig;
    let languageId!: number;
    this.store
      .pipe(
        select(selectRouter),
        withLatestFrom(this.store.pipe(select(getDotCmsLanguageConfiguration))),
        concatLatestFrom(() => this.store.pipe(select(getCurrentLocale)))
      )
      .subscribe(([[routerState, config], locale]) => {
        //state => state.router?.state?.queryParams?.language_id
        languageId = routerState?.state?.queryParams?.language_id;
        languageConfig = {
          languageId: config,
        };

        const languageMap = languageConfig;

        const matchedLocale = languageMap.languageId
          ? Object.keys(languageMap.languageId).find(value => +languageConfig?.languageId?.[value] === +languageId)
          : false;
        if (matchedLocale) {
          this.store.dispatch(
            applyConfiguration({
              lang: matchedLocale ?? locale,
            })
          );
        }
      });
  }

  setTransparentHeader(isTransparent = true) {
    this.store.dispatch(
      setIsTransparentHeader({
        isTransparentHeader: isTransparent
          ? (this.pageState()?.pageResponse?.content?.extraPageProps?.transparentHeader ?? false)
          : false,
      })
    );
  }

  getDotCmsHost$ = this.store.pipe(select(getCurrentDotCmsApiConfiguration)).pipe(map(config => config.host));

  getVideoHeight(contentlet: VideoContentlet): Observable<string> {
    return this.appFacade.deviceType$.pipe(
      map(deviceType => this.generateVideoHeightUsingDeviceType(contentlet, deviceType))
    );
  }

  private generateVideoHeightUsingDeviceType(contentlet: VideoContentlet, deviceType: DeviceType) {
    return deviceType === 'desktop'
      ? contentlet.heightlg
      : deviceType === 'tablet'
        ? contentlet.heightmd
        : deviceType === 'mobile'
          ? contentlet.heightsm
          : '';
  }

  getVideoClasses(contentlet: VideoContentlet): string {
    if (contentlet?.maxWidth) {
      return `${contentlet.cssClass}` + ` video-max-width`;
    }

    return contentlet.cssClass;
  }

  getPreviousUrl(): Observable<string> {
    return this.store.pipe(select(selectPreviousUrl));
  }

  currentPageUrl(): Observable<string> {
    return this.store.pipe(select(selectCleanUrl));
  }
  navigateTo(url: string) {
    this.router.navigate([url]);
  }
}
